import { GhostSelect } from '../ui/GhostSelect';
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle, 
  faTimes, 
  faLock, 
  faTrashAlt, 
  faSignOutAlt, 
  faEnvelopeOpenText, 
  faUnlink, 
  faShieldAlt,
  faArrowRight,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { fastapi } from '../../services/api';
import BtnTaratic from '../ui/BtnTaratic';
import { GhostInput } from '../ui/GhostInput';
import { useTranslation } from 'react-i18next';

interface DeleteCommerceModalProps {
  isOpen: boolean;
  onClose: () => void;
  commerceName: string;
}

const DeleteCommerceModal: React.FC<DeleteCommerceModalProps> = ({ isOpen, onClose, commerceName }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [deletionReason, setDeletionReason] = useState<string>('no_longer_needed');
  const [deletionComments, setDeletionComments] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmName, setConfirmName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const DELETION_REASONS = [
    { value: 'no_longer_needed', label: t('settings.general.deleteModal.reasons.no_longer_needed', 'Ya no necesito la plataforma') },
    { value: 'missing_features', label: t('settings.general.deleteModal.reasons.missing_features', 'Falta de funcionalidades necesarias') },
    { value: 'technical_issues', label: t('settings.general.deleteModal.reasons.technical_issues', 'Dificultades técnicas o de configuración') },
    { value: 'high_price', label: t('settings.general.deleteModal.reasons.high_price', 'Precio / Presupuesto elevado') },
    { value: 'switched_platform', label: t('settings.general.deleteModal.reasons.switched_platform', 'Me cambié a otra alternativa') },
    { value: 'other', label: t('settings.general.deleteModal.reasons.other', 'Otro motivo') },
  ];

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPassword('');
      setConfirmName('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirmationMatched = confirmName.trim().toUpperCase() === commerceName.trim().toUpperCase() || confirmName.trim().toUpperCase() === 'ELIMINAR';

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError(t('settings.general.deleteModal.errorPassword', 'Debes ingresar tu contraseña actual.'));
      return;
    }
    if (!isConfirmationMatched) {
      setError(t('settings.general.deleteModal.errorConfirm', 'Escribe "{{name}}" o "ELIMINAR" para confirmar.', { name: commerceName }));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await fastapi.post('/auth/delete-commerce', {
        password,
        deletion_reason: deletionReason,
        deletion_comments: deletionComments,
      });

      // Clear local authentication tokens and states
      localStorage.clear();
      sessionStorage.clear();

      // Redirect to login page with query parameter
      window.location.href = '/login?deleted=true';
    } catch (err: any) {
      console.error('Error deleting commerce:', err);
      const msg = err.response?.data?.detail || 'Error al procesar la eliminación del comercio.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setLoading(false);
    }
  };

  return (
    <div data-ui="delete-commerce-modal-region-001" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in overflow-y-auto">
      <div data-ui="delete-commerce-modal-region-002" className="relative w-full max-w-xl bg-black/90 border-none rounded-2xl shadow-2xl p-6 sm:p-8 my-8 overflow-hidden backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md">
        {/* Header con indicador de Paso */}
        <div data-ui="delete-commerce-modal-region-003" className="flex items-center justify-between pb-4 border-b border-white/10">
          <div data-ui="delete-commerce-modal-region-004" className="flex items-center gap-3">
            <div data-ui="delete-commerce-modal-region-005" className="w-10 h-10 rounded-xl bg-red-500/20 text-red-400 flex items-center justify-center shrink-0 border-none shadow-inner-glow">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl icon-inner-glow-white" />
            </div>
            <div data-ui="delete-commerce-modal-region-006">
              <div data-ui="delete-commerce-modal-region-007" className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">
                  {t('settings.general.deleteModal.title', 'Borrar Comercio')}
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[10px] font-mono font-bold uppercase border-none shadow-inner-glow">
                  {t('settings.general.deleteModal.step', 'Paso {{step}} de 2', { step })}
                </span>
              </div>
              <p className="text-xs text-white/80 mt-0.5">
                {step === 1
                  ? t('settings.general.deleteModal.step1Subtitle', 'Revisa las implicaciones y cuéntanos la razón de tu baja')
                  : t('settings.general.deleteModal.step2Subtitle', 'Confirmación de seguridad y autorización final')}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-all duration-300 p-2 rounded-xl hover:bg-white/10 shadow-inner-glow border-none"
            disabled={loading}
            title={t('common.close', 'Cerrar modal')}
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div data-ui="delete-commerce-modal-region-008" className="mt-4 p-3.5 bg-red-500/15 border-none rounded-xl text-red-300 text-xs sm:text-sm flex items-center gap-2.5 animate-fade-in shadow-inner-glow">
            <FontAwesomeIcon icon={faExclamationTriangle} className="shrink-0 text-red-400" />
            <span>{error}</span>
          </div>
        )}

        {/* ========================================================================= */}
        {/* PASO 1: MODAL DE IMPLICACIONES Y MOTIVO DE BAJA */}
        {/* ========================================================================= */}
        {step === 1 && (
          <div data-ui="delete-commerce-modal-region-009" className="mt-5 space-y-5 animate-fade-in">
            {/* Lista de Implicaciones Directas */}
            <div data-ui="delete-commerce-modal-region-010" className="space-y-2.5">
              <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider flex items-center gap-1.5">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>{t('settings.general.deleteModal.implicationsHeader', 'Implicaciones del borrado de cuenta:')}</span>
              </h4>

              <div data-ui="delete-commerce-modal-region-011" className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div data-ui="delete-commerce-modal-region-012" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow space-y-1">
                  <div data-ui="delete-commerce-modal-region-013" className="flex items-center gap-2 text-red-400 font-bold text-xs">
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span>{t('settings.general.deleteModal.impl1Title', 'Cierre de Sesión')}</span>
                  </div>
                  <p className="text-[11px] text-white/80 leading-relaxed font-medium">
                    {t('settings.general.deleteModal.impl1Desc', 'Tu sesión actual se cerrará inmediatamente y perderás acceso al panel.')}
                  </p>
                </div>

                <div data-ui="delete-commerce-modal-region-014" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow space-y-1">
                  <div data-ui="delete-commerce-modal-region-015" className="flex items-center gap-2 text-emerald-400 font-bold text-xs">
                    <FontAwesomeIcon icon={faEnvelopeOpenText} />
                    <span>{t('settings.general.deleteModal.impl2Title', 'Correo Libre')}</span>
                  </div>
                  <p className="text-[11px] text-white/80 leading-relaxed font-medium">
                    {t('settings.general.deleteModal.impl2Desc', 'Tu correo electrónico quedará disponible para registrar una nueva cuenta si lo deseas.')}
                  </p>
                </div>

                <div data-ui="delete-commerce-modal-region-016" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow space-y-1">
                  <div data-ui="delete-commerce-modal-region-017" className="flex items-center gap-2 text-yellow-400 font-bold text-xs">
                    <FontAwesomeIcon icon={faUnlink} />
                    <span>{t('settings.general.deleteModal.impl3Title', 'Desconexión Meta')}</span>
                  </div>
                  <p className="text-[11px] text-white/80 leading-relaxed font-medium">
                    {t('settings.general.deleteModal.impl3Desc', 'Se desvincularán los tokens de acceso de Instagram, Facebook y Webhooks.')}
                  </p>
                </div>

                <div data-ui="delete-commerce-modal-region-018" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow space-y-1">
                  <div data-ui="delete-commerce-modal-region-019" className="flex items-center gap-2 text-blue-400 font-bold text-xs">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <span>{t('settings.general.deleteModal.impl4Title', 'Custodia Legal Soft Delete')}</span>
                  </div>
                  <p className="text-[11px] text-white/80 leading-relaxed font-medium">
                    {t('settings.general.deleteModal.impl4Desc', 'Tus datos se archivan de forma segura en estado inactivo para la administración.')}
                  </p>
                </div>
              </div>
            </div>

            {/* Encuesta / Motivo */}
            <div data-ui="delete-commerce-modal-region-020" className="space-y-4 pt-1">
              <div data-ui="delete-commerce-modal-region-021">
                <label className="block text-xs font-semibold text-white/90 uppercase tracking-wider mb-1.5">
                  {t('settings.general.deleteModal.reasonLabel', '¿Cuál es el motivo principal de la baja?')}
                </label>
                <GhostSelect value={deletionReason} onChange={setDeletionReason} options={DELETION_REASONS} />
              </div>

              <div data-ui="delete-commerce-modal-region-022">
                <label className="block text-xs font-semibold text-white/90 uppercase tracking-wider mb-1.5">
                  {t('settings.general.deleteModal.commentsLabel', 'Comentarios adicionales o sugerencias (Opcional)')}
                </label>
                <textarea
                  value={deletionComments}
                  onChange={(e) => setDeletionComments(e.target.value)}
                  rows={2}
                  placeholder={t('settings.general.deleteModal.commentsPlaceholder', 'Cuéntanos cómo podríamos mejorar...')}
                  className="w-full bg-black/50 border-none text-white rounded-xl p-3 focus:outline-none focus:ring-1 focus:ring-red-400 transition-all text-xs sm:text-sm resize-none shadow-inner-glow placeholder-white/50"
                />
              </div>
            </div>

            {/* Botones de Navegación */}
            <div data-ui="delete-commerce-modal-region-023" className="pt-4 flex items-center justify-between gap-3 border-t border-white/10">
              <BtnTaratic color="ghost-dark" onClick={onClose} type="button">
                {t('common.cancel', 'Cancelar')}
              </BtnTaratic>

              <BtnTaratic 
                color="red" 
                onClick={() => setStep(2)} 
                type="button"
                className="flex items-center gap-2"
              >
                <span>{t('settings.general.deleteModal.continueButton', 'Entiendo las implicaciones y continuar')}</span>
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </BtnTaratic>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* PASO 2: SEGUNDO MODAL DE CONFIRMACIÓN FINAL Y VERIFICACIÓN */}
        {/* ========================================================================= */}
        {step === 2 && (
          <form onSubmit={handleDelete} className="mt-5 space-y-5 animate-fade-in">
            {/* Resumen del Comercio */}
            <div data-ui="delete-commerce-modal-region-024" className="p-4 bg-red-500/10 border-none rounded-xl space-y-2 text-xs leading-relaxed shadow-inner-glow">
              <div data-ui="delete-commerce-modal-region-025" className="flex items-center gap-2 font-bold text-red-300 text-sm">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{t('settings.general.deleteModal.finalConfirmation', 'Confirmación Definitiva')}</span>
              </div>
              <p className="text-white/90 font-medium">
                {t('settings.general.deleteModal.authorizingText', 'Estás autorizando el borrado del comercio:')}{' '}
                <strong className="text-white font-mono bg-black/60 px-2 py-0.5 rounded shadow-inner-glow">{commerceName}</strong>.
              </p>
              <p className="text-white/80 text-[11px] font-medium">
                {t('settings.general.deleteModal.redirectNotice', 'Una vez confirmada la contraseña, serás redirigido a la pantalla de login con tu correo liberado.')}
              </p>
            </div>

            {/* Campo 1: Contraseña Actual */}
            <div data-ui="delete-commerce-modal-region-026">
              <label className="block text-xs font-semibold text-white/90 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faLock} className="text-red-400 text-xs" />
                <span>{t('settings.general.deleteModal.passwordLabel', '1. Ingresa tu contraseña actual:')}</span>
              </label>
              <GhostInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('settings.general.deleteModal.passwordPlaceholder', 'Tu contraseña para verificar identidad')}
                required
                className="w-full text-xs sm:text-sm"
              />
            </div>

            {/* Campo 2: Nombre de Confirmación */}
            <div data-ui="delete-commerce-modal-region-027">
              <label className="block text-xs font-semibold text-white/90 uppercase tracking-wider mb-1.5">
                {t('settings.general.deleteModal.confirmNameLabel', '2. Escribe "{{name}}" o "ELIMINAR" para confirmar:', { name: commerceName })}
              </label>
              <GhostInput
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={t('settings.general.deleteModal.confirmNamePlaceholder', 'Escribe ELIMINAR para confirmar')}
                required
                className="w-full text-xs sm:text-sm"
              />
            </div>

            {/* Botones Finales */}
            <div data-ui="delete-commerce-modal-region-028" className="pt-4 flex items-center justify-between border-t border-white/10 gap-3">
              <BtnTaratic 
                color="ghost-dark" 
                onClick={() => setStep(1)} 
                type="button" 
                disabled={loading}
              >
                {t('common.back', 'Atrás')}
              </BtnTaratic>

              <div data-ui="delete-commerce-modal-region-029" className="flex gap-2">
                <BtnTaratic 
                  color="ghost-dark" 
                  onClick={onClose} 
                  type="button" 
                  disabled={loading}
                >
                  {t('common.cancel', 'Cancelar')}
                </BtnTaratic>

                <BtnTaratic
                  color="red"
                  type="submit"
                  disabled={loading || !password || !isConfirmationMatched}
                  className="flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                  <span>
                    {loading
                      ? t('settings.general.deleteModal.deleting', 'Eliminando...')
                      : t('settings.general.deleteModal.confirmDeleteButton', 'Confirmar y Borrar Comercio')}
                  </span>
                </BtnTaratic>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DeleteCommerceModal;

